import {Api} from '@shared/constants/common-field.constants';

/**
 * One platform-link registration: the URL the router owns and the command that URL sends.
 *
 * These used to be written out as statements in `AdminPanelModule`'s constructor. They are a table
 * here so a spec can check every registered URL against `adminPanelRoutes` without constructing
 * the module — importing `AdminPanelModule` into a karma spec pulls in the whole AdminPanel
 * component graph and hangs the browser before a single test runs. A registration that is not
 * reachable by route sends its command and then falls through to `/`, which is how the SCIM
 * Clients page (IDP-E-62-09) shipped unreachable; `admin-panel.routes.spec.ts` is what stops that
 * happening again, and it only works if every registration lives in this list.
 */
export interface AdminPanelLinkCommandDef {
  /** The router URL, exactly as passed to `registrPlatformLinkCommand`. */
  url: string;
  /** The backend command name. */
  command: string;
  /** Which API the command is sent to. */
  api: string;
  /** Values set on the command before it is registered (e.g. the tab an entry opens on). */
  values?: { [key: string]: any };
}

export const adminPanelLinkCommands: AdminPanelLinkCommandDef[] = [
  {url: '/admin-panel/user', command: 'UserToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/company', command: 'AdminPanelCompanyViewToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/company/:companyId', command: 'AdminPanelCompanyDetailToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/company-360', command: 'AdminDashboardCompaniesToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/company-360/:companyId', command: 'AdminDashboardCompany360ToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/dashboard-settings', command: 'AdminDashboardSettingsToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/user/:userName', command: 'AdminPanelUserDetailToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/online-users', command: 'OnlineUserCompaniesToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/client-manager', command: 'ClientManagerToolLinkCommand', api: Api.AdminPanel},

  // SCIM partner provisioning (IDP-E-62). The landing grid of companies, and each company's page
  // under its own URL (IDP-E-76-07). The gate is the APManagement policy, which ITP-API enforces on
  // every command these pages send.
  {url: '/admin-panel/scim-clients', command: 'ScimClientsToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/scim-clients/:companyId', command: 'ScimClientsToolLinkCommand', api: Api.AdminPanel},

  {
    url: '/admin-panel/in-app-purchase',
    command: 'InAppPurchaseToolLinkCommand',
    api: Api.AdminPanel,
    values: {SelectedTabId: 'UserAccount'}
  },
  {
    url: '/admin-panel/in-app-purchase/user',
    command: 'InAppPurchaseToolLinkCommand',
    api: Api.AdminPanel,
    values: {SelectedTabId: 'UserAccount'}
  },
  {
    url: '/admin-panel/in-app-purchase/company',
    command: 'InAppPurchaseToolLinkCommand',
    api: Api.AdminPanel,
    values: {SelectedTabId: 'CompanyAccount'}
  },
  {
    url: '/admin-panel/in-app-purchase/firm',
    command: 'InAppPurchaseToolLinkCommand',
    api: Api.AdminPanel,
    values: {SelectedTabId: 'FirmAccount'}
  },
  {
    url: '/admin-panel/in-app-purchase/pending',
    command: 'InAppPurchaseToolLinkCommand',
    api: Api.AdminPanel,
    values: {SelectedTabId: 'PendingRequest'}
  },

  {url: '/admin-panel/company-snapshot', command: 'CompanySnapshotToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/administrative-modification', command: 'AdminModificationToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/merge', command: 'MergeLicenseToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/gateway', command: 'ApiGatewayToolLinkCommand', api: Api.AdminPanel},
  {url: '/admin-panel/pod-size', command: 'PodSizeToolLinkCommand', api: Api.AdminPanel},

  {url: '/admin-panel/email-blast', command: 'EmailBlastToolLinkCommand', api: Api.TaxHubApi},
  {url: '/email-blast/:emailBlastId', command: 'EmailBlastDetailsToolLinkCommand', api: Api.TaxHubApi},
  {url: '/admin-panel/email-blast/email-thread', command: 'CreateOpenEmailThreadLinkCommand', api: Api.TaxHubApi},

  {url: '/sandbox', command: 'SandBoxToolLinkCommand', api: Api.AdminPanel},
  {url: '/sandbox/:sandboxId', command: 'SandboxUserListCommand', api: Api.AdminPanel},

  {url: '/rfp-library', command: 'RfpLibraryToolLinkCommand', api: Api.AdminPanel},
  {url: '/rfp-cases', command: 'RfpCasesToolLinkCommand', api: Api.AdminPanel},
  {url: '/rfp-cases/:CaseId', command: 'ReviewCaseToolLinkCommand', api: Api.AdminPanel},

  {url: '/admin-panel/billing-accounts', command: 'BillingAccountsToolLinkCommand', api: Api.MarketPlace},

  {url: '/', command: 'AdminDashboardToolLinkCommand', api: Api.AdminPanel}
];
